import { FC, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { createColumnHelper } from '@tanstack/react-table';
import DataTable from '@/components/functional/DataTable/DataTable.tsx';
import LoaderWrapper from '@/components/ui/LoaderWrapper/LoaderWrapper.tsx';
import useAdminEscalations, { type AdminEscalation } from '@/api/admin/useAdminEscalations.ts';
import Routes from '@/router/Routes.ts';

const columnHelper = createColumnHelper<AdminEscalation>();

function buildColumns(onOpen: (id: string) => void) {
  return [
    columnHelper.accessor('organization_name', {
      header: () => 'Operating Entity',
      cell: (info) => <span className="font-dm font-bold text-sm text-[#2b3440]">{info.getValue() ?? '—'}</span>,
    }),
    columnHelper.accessor('issue_title', {
      header: () => 'Governed Decision',
      cell: (info) => <span className="font-dm text-sm text-[#2b3440]">{info.getValue() ?? '—'}</span>,
    }),
    columnHelper.accessor('primary_framework', {
      header: () => 'Framework',
      cell: (info) => <span className="font-dm text-sm text-[#2b3440]">{info.getValue() ?? '—'}</span>,
    }),
    columnHelper.accessor('governance_score', {
      header: () => 'Gov. Score',
      cell: (info) => {
        const v = info.getValue();
        return <span className="font-dm font-bold text-sm text-[#2b3440]">{v ?? '—'}</span>;
      },
    }),
    columnHelper.accessor('pending_escalation_count', {
      header: () => 'Governance Escalations',
      cell: (info) => (
        <span className="inline-flex items-center justify-center min-w-[24px] h-6 px-2 rounded-full bg-red-100 text-red-600 font-dm font-bold text-[12px]">
          {info.getValue()}
        </span>
      ),
    }),
    columnHelper.accessor('created_at', {
      header: () => 'Submitted',
      cell: (info) => (
        <span className="font-dm text-sm text-[#2b3440] whitespace-nowrap">
          {dayjs(info.getValue()).format('MMM D, YYYY')}
        </span>
      ),
    }),
    columnHelper.display({
      id: 'action',
      header: () => 'Action',
      enableSorting: false,
      cell: (info) => (
        <button
          onClick={() => onOpen(info.row.original.id)}
          className="font-dm font-bold text-sm text-[#0f1b2a] underline underline-offset-2 hover:opacity-70 transition-opacity whitespace-nowrap"
        >
          Review
        </button>
      ),
    }),
  ];
}

const AdminEscalationsPage: FC = () => {
  const navigate = useNavigate();
  const { data: escalations = [], isLoading } = useAdminEscalations();

  const columns = useMemo(
    () => buildColumns((id) => navigate(Routes.ADMIN_SESSION_DETAIL.replace(':id', id))),
    [navigate],
  );

  return (
    <div className="flex flex-col gap-8">
      <div className="flex items-center px-6 py-4 bg-[#f4f9ff] rounded-xl">
        <div className="flex flex-col gap-1">
          <h1 className="font-newsreader text-[28px] font-extrabold leading-[125%] text-[rgba(0,0,0,0.87)]">
            Governance Escalations
          </h1>
          <p className="font-dm text-[14px] font-normal leading-[150%] tracking-[0.15px] text-[rgba(0,0,0,0.6)]">
            Fiduciary escalations pending institutional review.
          </p>
        </div>
        {escalations.length > 0 && (
          <span className="ml-auto inline-flex items-center justify-center min-w-[28px] h-7 px-2 rounded-full bg-[#fff1f0] text-[#d93025] font-dm font-bold text-sm">
            {escalations.length}
          </span>
        )}
      </div>

      {isLoading ? (
        <LoaderWrapper isLoading />
      ) : escalations.length === 0 ? (
        <p className="font-dm text-sm text-center text-[#2b3440]/50 py-10">No governance escalations found.</p>
      ) : (
        <DataTable
          data={escalations}
          columns={columns}
          zebra
          className="table-auto"
          wrapperClassName="w-full rounded-2xl border border-[rgba(204,204,204,0.5)] bg-base-100"
        />
      )}
    </div>
  );
};

export default AdminEscalationsPage;
